import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  ScaleIcon,
  ExclamationCircleIcon,
  AcademicCapIcon,
  ListBulletIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BookOpenIcon,
  DocumentTextIcon,
  TrophyIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  ShieldExclamationIcon,
  BanknotesIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { useGovernanceStats, cddTierCount, riskOpenCount, ttrOpenCount } from '../../hooks/useGovernanceStats'
import { useFeatureFlags } from '../../contexts/FeatureFlagContext'

/* ================================================================
   VENUES — empty until /api/venues loads
   ================================================================ */
function toCanonicalVenueId(venueId) {
  if (!venueId) return venueId
  const s = String(venueId).toLowerCase()
  if (s === 'diggers' || s.endsWith('diggers')) return 'diggers'
  if (s === 'leagues' || s.endsWith('leagues')) return 'leagues'
  if (s === 'golf' || s.endsWith('golf')) return 'golf'
  if (s === 'sports' || s.endsWith('sports')) return 'sports'
  return s
}

function useVenues() {
  const [venues, setVenues] = useState([])
  useEffect(() => {
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && j.data.length > 0) {
          setVenues(
            j.data.map((v) => ({
              id: toCanonicalVenueId(v.venue_id),
              name: v.name,
              shortName: v.code || v.name?.split(' ')[0] || v.venue_id,
            }))
          )
        }
      } catch {}
    })()
  }, [])
  return venues
}


const CATEGORY_LABELS = {
  enrolment: 'Registration & Enrolment',
  compliance_officer: 'Compliance Officer',
  risk_assessment: 'ML/TF Risk Assessment',
  amlctf_program: 'AML/CTF Program',
  reporting: 'Reporting',
  training: 'Training',
  employee_dd: 'Employee Due Diligence',
}

const CHECKLIST_STATUS = {
  not_started: { label: 'Not Started', color: '#64748b', bg: '#f1f5f9' },
  in_progress: { label: 'In Progress', color: '#2563eb', bg: '#eff6ff' },
  completed: { label: 'Completed', color: '#16a34a', bg: '#f0fdf4' },
  overdue: { label: 'Overdue', color: '#dc2626', bg: '#fef2f2' },
}

const DOC_STATUS = {
  current: { label: 'Current', color: '#16a34a', bg: '#f0fdf4' },
  review_due: { label: 'Review Due', color: '#ca8a04', bg: '#fefce8' },
}

const REVIEW_STATUS = {
  satisfactory: { label: 'Satisfactory', color: '#16a34a', bg: '#f0fdf4' },
  satisfactory_with_findings: { label: 'With Findings', color: '#ca8a04', bg: '#fefce8' },
}

/* ================================================================
   STAT CARD
   ================================================================ */
function StatCard({ title, value, subtitle, icon: Icon, trend, trendDirection, color, accentBg }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-md transition-all duration-200 text-left w-full">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-[12px] font-medium uppercase tracking-wider mb-2 text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-3xl font-bold tracking-tight text-psg-teal dark:text-psg-teal-300">{value}</p>
          {subtitle && (
            <p className="text-[14px] mt-1 flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
              {trend && (
                <span className={`inline-flex items-center gap-0.5 text-[12px] font-semibold ${trendDirection === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                  {trendDirection === 'up' ? <ArrowTrendingUpIcon className="w-3 h-3" /> : <ArrowTrendingDownIcon className="w-3 h-3" />}
                  {trend}
                </span>
              )}
              {subtitle}
            </p>
          )}
        </div>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: accentBg }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </div>
  )
}

/* ================================================================
   CARDS
   ================================================================ */
function ObligationStatusDot({ status }) {
  const colors = { current: '#16a34a', due_soon: '#ca8a04', overdue: '#dc2626' }
  return <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[status] || '#6B7280' }} />
}

function RegulatoryObligationsCard({ obligations, categoryLabels }) {
  const grouped = useMemo(() => {
    const g = {}
    obligations.forEach((o) => {
      if (!g[o.category]) g[o.category] = []
      g[o.category].push(o)
    })
    return g
  }, [obligations])

  const currentCount = obligations.filter((o) => o.status === 'current').length
  const dueSoonCount = obligations.filter((o) => o.status === 'due_soon').length
  const overdueCount = obligations.filter((o) => o.status === 'overdue').length

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="px-5 py-4 flex items-center justify-between border-b border-gray-50 dark:border-gray-700">
        <h3 className="text-[14px] font-semibold flex items-center gap-2 text-psg-teal dark:text-psg-teal-300">
          <ScaleIcon className="w-4 h-4 text-psg-teal" />
          Regulatory Obligations
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-[12px] px-2 py-0.5 rounded-full font-medium bg-green-50 dark:bg-green-900/20 text-green-600">{currentCount} ✓</span>
          <span className="text-[12px] px-2 py-0.5 rounded-full font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-600">{dueSoonCount} !</span>
          <span className="text-[12px] px-2 py-0.5 rounded-full font-medium bg-red-50 dark:bg-red-900/20 text-red-600">{overdueCount} ✗</span>
        </div>
      </div>
      <div className="px-5 py-3 grid grid-cols-2 gap-x-6 gap-y-3">
        {Object.entries(grouped).map(([cat, items]) => (
          <div key={cat}>
            <p className="text-[12px] font-semibold uppercase tracking-wider mb-1.5 pt-[5px] text-gray-500 dark:text-gray-400">
              {categoryLabels[cat] || cat}
            </p>
            <div className="space-y-1">
              {items.map((ob) => (
                <div key={ob.id} className="flex items-center gap-2">
                  <ObligationStatusDot status={ob.status} />
                  <span className="text-[13px] text-gray-900 dark:text-gray-100 truncate">{ob.name}</span>
                  {ob.dueDate && (
                    <span className={`text-[11px] ml-auto flex-shrink-0 ${ob.status === 'overdue' ? 'text-red-600' : ob.status === 'due_soon' ? 'text-amber-600' : 'text-gray-400 dark:text-gray-500'}`}>
                      {ob.dueDate}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ChecklistProgressCard({ checklists, onViewAll }) {
  const open = checklists.filter((c) => c.status === 'in_progress' || c.status === 'not_started')
  const toShow = open.length > 0 ? open : checklists.slice(0, 4)
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="px-5 py-4 flex items-center justify-between border-b border-gray-50 dark:border-gray-700">
        <h3 className="text-[14px] font-semibold flex items-center gap-2 text-psg-teal dark:text-psg-teal-300">
          <ListBulletIcon className="w-4 h-4 text-indigo-500" />
          Active Checklists
        </h3>
        <button onClick={onViewAll} className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-psg-teal/10 text-psg-teal hover:bg-psg-teal/20 transition-colors">
          View All →
        </button>
      </div>
      <div className="divide-y divide-gray-50 dark:divide-gray-700">
        {toShow.length === 0 ? (
          <div className="px-5 py-6 text-center text-[14px] text-gray-500 dark:text-gray-400">No checklists yet</div>
        ) : toShow.slice(0, 4).map((cl) => {
          const pct = cl.total > 0 ? Math.round((cl.progress / cl.total) * 100) : 0
          const sc = CHECKLIST_STATUS[cl.status] || CHECKLIST_STATUS.not_started
          return (
            <div key={cl.id} className="px-5 py-3.5 hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer" onClick={onViewAll}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">{cl.name}</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>
                  {sc.label}
                </span>
              </div>
              <div className="flex items-center gap-3 mb-1.5">
                <div className="flex-1 h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-600">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#16a34a' : '#2563eb' }} />
                </div>
                <span className={`text-[12px] font-bold tabular-nums ${pct === 100 ? 'text-green-600' : 'text-gray-600 dark:text-gray-400'}`}>
                  {cl.progress}/{cl.total}
                </span>
              </div>
              <p className="text-[12px] text-gray-500 dark:text-gray-400">Due: {cl.dueDate} · {cl.assignedTo}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TrainingCoverageCard({ training }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-50 dark:border-gray-700">
        <h3 className="text-[14px] font-semibold flex items-center gap-2 text-psg-teal dark:text-psg-teal-300">
          <AcademicCapIcon className="w-4 h-4 text-purple-500" />
          Training Coverage
        </h3>
      </div>
      <div className="divide-y divide-gray-50 dark:divide-gray-700">
        {training.map((t) => {
          const pct = t.total > 0 ? Math.round((t.completed / t.total) * 100) : 0
          return (
            <div key={t.moduleId} className="px-5 py-3.5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[14px] font-medium text-gray-900 dark:text-gray-100">{t.moduleName}</span>
                <span className={`text-[14px] font-bold tabular-nums ${pct === 100 ? 'text-green-600' : 'text-amber-600'}`}>{pct}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden mb-1.5 bg-gray-100 dark:bg-gray-600">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#16a34a' : '#ca8a04' }} />
              </div>
              <p className="text-[12px] text-gray-500 dark:text-gray-400">{t.completed}/{t.total} staff · Next due: {t.nextDue}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ProgramDocumentsCard({ documents }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-50 dark:border-gray-700">
        <h3 className="text-[14px] font-semibold flex items-center gap-2 text-psg-teal dark:text-psg-teal-300">
          <DocumentTextIcon className="w-4 h-4 text-psg-teal" />
          AML/CTF Program Documents
        </h3>
      </div>
      <div className="divide-y divide-gray-50 dark:divide-gray-700">
        {documents.slice(0, 4).map((doc) => {
          const sc = DOC_STATUS[doc.status] || DOC_STATUS.current
          return (
            <div key={doc.id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-medium text-gray-900 dark:text-gray-100">{doc.name}</span>
                  <span className="text-[12px] font-mono text-gray-500 dark:text-gray-400">v{doc.version}</span>
                </div>
                <p className="text-[12px] mt-0.5 text-gray-500 dark:text-gray-400">{doc.approvedDate ? `Approved ${doc.approvedDate} by ` : 'Approved by '}{doc.approvedBy || 'Board'}</p>
              </div>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>
                {sc.label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function IndependentReviewCard({ reviews }) {
  const nextReview = reviews.length ? reviews.reduce((min, r) => (r.nextReviewDue < min ? r.nextReviewDue : min), reviews[0]?.nextReviewDue || '') : ''
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-50 dark:border-gray-700">
        <h3 className="text-[14px] font-semibold flex items-center gap-2 text-psg-teal dark:text-psg-teal-300">
          <TrophyIcon className="w-4 h-4 text-cyan-500" />
          Independent Reviews
        </h3>
      </div>
      <div className="divide-y divide-gray-50 dark:divide-gray-700">
        {reviews.slice(0, 2).map((r) => {
          const sc = REVIEW_STATUS[r.status] || { label: r.status, color: '#6B7280', bg: '#f3f4f6' }
          return (
            <div key={r.id} className="px-5 py-3.5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">FY {r.period}</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>
                  <CheckCircleIcon className="w-3 h-3" /> {sc.label}
                </span>
              </div>
              <p className="text-[12px] text-gray-500 dark:text-gray-400">{r.reviewerOrg}{r.completedDate ? ` · ${r.completedDate}` : ''}</p>
              <p className="text-[12px] mt-0.5 text-gray-500 dark:text-gray-400">
                {r.findingsCritical + r.findingsMajor + r.findingsMinor} findings · {r.findingsResolved} resolved
              </p>
            </div>
          )
        })}
      </div>
      {nextReview && (
        <div className="px-5 py-3 border-t border-gray-50 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/30">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="w-3 h-3 text-gray-500" />
            <span className="text-[12px] font-medium text-gray-600 dark:text-gray-400">Next independent review due: {nextReview}</span>
          </div>
        </div>
      )}
    </div>
  )
}

function ComplianceScoreCard({ obligations, trainingPct, docsCurrent, docsTotal }) {
  const metCount = obligations.filter((o) => o.status === 'current').length
  const totalCount = obligations.length
  const obligationPct = totalCount > 0 ? Math.round((metCount / totalCount) * 100) : 0
  const docPct = docsTotal > 0 ? Math.round((docsCurrent / docsTotal) * 100) : 0
  const overallScore = Math.round(obligationPct * 0.5 + trainingPct * 0.3 + docPct * 0.2)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-50 dark:border-gray-700">
        <h3 className="text-[14px] font-semibold flex items-center gap-2 text-psg-teal dark:text-psg-teal-300">
          <ChartBarIcon className="w-4 h-4 text-green-600" />
          Overall Compliance Score
        </h3>
      </div>
      <div className="p-5 flex flex-col items-center">
        <div className="relative w-32 h-32">
          <svg viewBox="0 0 120 120" className="w-full h-full">
            <circle cx="60" cy="60" r="50" fill="none" stroke="#f1f5f9" strokeWidth="10" />
            <circle
              cx="60" cy="60" r="50" fill="none"
              stroke={overallScore >= 80 ? '#16a34a' : overallScore >= 60 ? '#ca8a04' : '#dc2626'}
              strokeWidth="10"
              strokeDasharray={`${overallScore * 3.14} ${100 * 3.14}`}
              strokeLinecap="round"
              transform="rotate(-90 60 60)"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold text-psg-teal dark:text-psg-teal-300">{overallScore}%</span>
            <span className={`text-[12px] ${overallScore >= 80 ? 'text-green-600' : overallScore >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
              {overallScore >= 80 ? 'Good' : overallScore >= 60 ? 'Fair' : 'Needs Work'}
            </span>
          </div>
        </div>
        <div className="mt-4 w-full space-y-2">
          {[
            { label: 'Obligations Met', value: `${metCount}/${totalCount}`, color: obligationPct >= 80 ? '#16a34a' : '#ca8a04' },
            { label: 'Training Complete', value: `${trainingPct}%`, color: trainingPct >= 90 ? '#16a34a' : '#ca8a04' },
            { label: 'Documents Current', value: `${docsCurrent}/${docsTotal}`, color: docPct >= 80 ? '#16a34a' : '#ca8a04' },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between">
              <span className="text-[12px] text-gray-500 dark:text-gray-400">{item.label}</span>
              <span className="text-[12px] font-bold" style={{ color: item.color }}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function QuickActions({ navigate }) {
  const actions = [
    { label: 'Risk Assessment', icon: ScaleIcon, href: '/governance-safe/risk', color: '#0a5169', bg: '#e8f2f6' },
    { label: 'Board Report', icon: ArrowDownTrayIcon, href: '#', color: '#7c3aed', bg: '#f5f3ff' },
    { label: 'Start Checklist', icon: ListBulletIcon, href: '/governance-safe/checklist', color: '#6366f1', bg: '#eef2ff' },
    { label: 'Log Training', icon: AcademicCapIcon, href: '/governance-safe/training', color: '#0891b2', bg: '#ecfeff' },
  ]

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-50 dark:border-gray-700">
        <h3 className="text-[14px] font-semibold text-psg-teal dark:text-psg-teal-300">Quick Actions</h3>
      </div>
      <div className="p-3 grid grid-cols-2 gap-2">
        {actions.map((a) => {
          const Icon = a.icon
          return (
            <button
              key={a.label}
              onClick={() => a.href !== '#' && navigate(a.href)}
              className="flex items-center gap-2.5 p-3 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-sm transition-all text-left"
              style={{ backgroundColor: a.bg }}
            >
              <Icon className="w-4 h-4" style={{ color: a.color }} />
              <span className="text-[12px] font-medium text-gray-800 dark:text-gray-200">{a.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ================================================================
   MAIN
   ================================================================ */
export default function GovernanceDashboard() {
  const navigate = useNavigate()
  const { isPageEnabled } = useFeatureFlags()
  const ttrRegistryEnabled = isPageEnabled('/compliance-safe/ttr-registry')
  const venues = useVenues()
  const [venueFilter, setVenueFilter] = useState('all')
  const [obligations, setObligations] = useState([])
  const [checklists, setChecklists] = useState([])
  const [training, setTraining] = useState([])
  const [documents, setDocuments] = useState([])
  const [reviews, setReviews] = useState([])
  const { data: govStats } = useGovernanceStats()

  // Load governance data from aggregated dashboard API
  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(getLocalApiUrl('/api/governance/dashboard?year=2025-26')).then(r => r.json()).catch(() => ({ success: false }))
        if (res.success && res.data) {
          const d = res.data
          setObligations(d.obligations || [])
          setChecklists(d.checklists || [])
          setTraining(d.training || [])
          setDocuments(d.documents || [])
          setReviews(d.reviews || [])
        }
      } catch (_) {}
    }
    load()
  }, [])

  const filteredObligations = useMemo(
    () => (venueFilter === 'all' ? obligations : obligations.filter((o) => o.venueId === venueFilter)),
    [venueFilter, obligations]
  )
  const filteredChecklists = useMemo(
    () => (venueFilter === 'all' ? checklists : checklists.filter((c) => c.venueId === venueFilter)),
    [venueFilter, checklists]
  )
  const filteredDocuments = useMemo(
    () => (venueFilter === 'all' ? documents : documents.filter((d) => d.venueId === venueFilter)),
    [venueFilter, documents]
  )
  const filteredReviews = useMemo(
    () => (venueFilter === 'all' ? reviews : reviews.filter((r) => r.venueId === venueFilter)),
    [venueFilter, reviews]
  )

  const overdueItems = filteredObligations.filter((o) => o.status === 'overdue').length
  const openChecklists = filteredChecklists.filter((c) => c.status === 'in_progress' || c.status === 'not_started').length
  const inProgressChecklists = filteredChecklists.filter((c) => c.status === 'in_progress').length
  const totalTrainingCompleted = training.reduce((s, t) => s + t.completed, 0)
  const totalTrainingRequired = training.reduce((s, t) => s + t.total, 0)
  const trainingPct = totalTrainingRequired > 0 ? Math.round((totalTrainingCompleted / totalTrainingRequired) * 100) : 0
  const currentDocs = filteredDocuments.filter((d) => d.status === 'current').length
  const compliancePct = filteredObligations.length > 0
    ? Math.round((filteredObligations.filter((o) => o.status === 'current').length / filteredObligations.length) * 100)
    : 0

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header — no solid green panel */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex gap-2 items-center">
            <BookOpenIcon className="w-6 h-6 text-psg-teal" />
            Governance Safe
          </h1>
          <p className="text-[14px] text-gray-500 dark:text-gray-400 mt-1">
            Regulatory obligations, checklists, risk assessment, training & AML/CTF program
          </p>
        </div>
        <div className="flex items-center gap-2">
          <GlobeAltIcon className="w-4 h-4 text-gray-500" />
          <select
            value={venueFilter}
            onChange={(e) => setVenueFilter(e.target.value)}
            className="select-field w-auto min-w-[160px] text-[14px]"
          >
            <option value="all">All Venues</option>
            {venues.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Compliance Score"
          value={`${compliancePct}%`}
          subtitle="vs last quarter"
          trend="+4%"
          trendDirection="up"
          icon={ChartBarIcon}
          color="#16a34a"
          accentBg="#f0fdf4"
        />
        <StatCard
          title="Overdue Items"
          value={overdueItems}
          subtitle={overdueItems === 1 ? 'Item needs attention' : overdueItems > 1 ? `${overdueItems} items need attention` : 'All clear'}
          icon={ExclamationCircleIcon}
          color="#dc2626"
          accentBg="#fef2f2"
        />
        <StatCard
          title="Training Rate"
          value={`${trainingPct}%`}
          subtitle={`${totalTrainingCompleted} of ${totalTrainingRequired} complete`}
          icon={AcademicCapIcon}
          color="#7c3aed"
          accentBg="#f5f3ff"
        />
        <StatCard
          title="Open Checklists"
          value={openChecklists}
          subtitle={`${inProgressChecklists} in progress`}
          icon={ListBulletIcon}
          color="#6366f1"
          accentBg="#eef2ff"
        />
      </div>

      {/* Compliance Intelligence — P1 surface data (I12) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[12px] font-semibold tracking-wider uppercase text-gray-500 dark:text-gray-400">
            Compliance Intelligence
          </h2>
          <span className="text-[11px] text-gray-400 dark:text-gray-500">
            Risk register · TTR · rules · CDD tiers
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            type="button"
            onClick={() => navigate('/governance-safe/risk-register')}
            className="text-left"
          >
            <StatCard
              title="Risk Register"
              value={riskOpenCount(govStats)}
              subtitle={
                govStats.riskRegister.overdue_reviews > 0
                  ? `${govStats.riskRegister.overdue_reviews} overdue review${govStats.riskRegister.overdue_reviews === 1 ? '' : 's'}`
                  : 'Reviews current'
              }
              icon={ShieldExclamationIcon}
              color="#dc2626"
              accentBg="#fef2f2"
            />
          </button>
          {ttrRegistryEnabled && (
            <button
              type="button"
              onClick={() => navigate('/compliance-safe/ttr-registry')}
              className="text-left"
            >
              <StatCard
                title="TTR Open"
                value={ttrOpenCount(govStats)}
                subtitle={
                  govStats.ttrRegistry.overdue_drafts > 0
                    ? `${govStats.ttrRegistry.overdue_drafts} overdue · ${govStats.ttrRegistry.filed_last_7_days} filed 7d`
                    : `${govStats.ttrRegistry.filed_last_7_days} filed last 7 days`
                }
                icon={BanknotesIcon}
                color="#ca8a04"
                accentBg="#fefce8"
              />
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/monitor-safe/rules')}
            className="text-left"
          >
            <StatCard
              title="Rules Need Review"
              value={govStats.rules.published_needing_review}
              subtitle={
                govStats.rules.with_backfill_rationale > 0
                  ? `${govStats.rules.with_backfill_rationale} on backfill rationale`
                  : govStats.rules.missing_rationale > 0
                    ? `${govStats.rules.missing_rationale} missing rationale`
                    : 'All published rules reviewed'
              }
              icon={QuestionMarkCircleIcon}
              color="#7c3aed"
              accentBg="#f5f3ff"
            />
          </button>
          <StatCard
            title="High-Risk Patrons"
            value={cddTierCount(govStats, 'enhanced') + cddTierCount(govStats, 'prohibited')}
            subtitle={
              cddTierCount(govStats, 'prohibited') > 0
                ? `${cddTierCount(govStats, 'prohibited')} prohibited · ${cddTierCount(govStats, 'enhanced')} enhanced`
                : `${cddTierCount(govStats, 'enhanced')} enhanced · ${cddTierCount(govStats, 'standard')} standard`
            }
            icon={UserGroupIcon}
            color="#0369a1"
            accentBg="#eff6ff"
          />
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <RegulatoryObligationsCard obligations={filteredObligations} categoryLabels={CATEGORY_LABELS} />
          <ChecklistProgressCard checklists={filteredChecklists} onViewAll={() => navigate('/governance-safe/checklist')} />
          <ProgramDocumentsCard documents={filteredDocuments} />
        </div>
        <div className="space-y-6">
          <ComplianceScoreCard
            obligations={filteredObligations}
            trainingPct={trainingPct}
            docsCurrent={currentDocs}
            docsTotal={filteredDocuments.length}
          />
          <TrainingCoverageCard training={training} />
          <IndependentReviewCard reviews={filteredReviews} />
          <QuickActions navigate={navigate} />
        </div>
      </div>
    </div>
  )
}
